/* eslint-disable complexity */
import { until } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, watch } from 'vue';
import { type Lab } from '~/models/labs';
import type { User, UserContract } from '~/models/me';
import $fetch from '~/modules/fetch';
import timezones from '/assets/json/tz-cities-to-countries.json';

let fetchMePromise: Promise<User | undefined> | undefined;

type IMeStore = {
  me?: User;
  isMeFetched: boolean;
  contracts: UserContract[];
  externalPartContracts: UserContract[];
  shops: Lab[];
  currentShop: string;
  isMyAppsSynchronizing: boolean;
  myRequests: any[];
  shouldShowCartRecupModal: boolean;
  company: any;
  addresses: any[];
  countries: any[];
  suppliedContracts: any[];
  privacyContracts: any[];
  estimatedUserLocation: string;
  enterpriseServices: any[];
};

export const useMeStore = defineStore({
  id: 'me',
  state: (): IMeStore => ({
    me: undefined,
    isMeFetched: false,
    contracts: [],
    externalPartContracts: [],
    shops: [],
    currentShop: '',
    isMyAppsSynchronizing: false,
    myRequests: [],
    shouldShowCartRecupModal: false,
    company: {},
    addresses: [],
    countries: [],
    suppliedContracts: [],
    privacyContracts: [],
    estimatedUserLocation: '',
    enterpriseServices: [],
  }),
  getters: {
    isLoggedIn(s): boolean {
      return Boolean(s.me);
    },
    isOperator(s) {
      return Boolean(s.me?.PersonRoles.find((el) => el.Role === 'DSOperator'));
    },
    isPrivateAdmin(s) {
      return Boolean(s.me?.PersonRoles.find((el) => el.Project !== 'Public'));
    },
    address(): (id: string) => any {
      return (id: string) => this.flattenAddresses.find((el) => el?.id === id) || {};
    },
    flattenAddresses(s): any[] {
      return [
        s.addresses,
        (s.company.addresses || []).map((el: any) => ({ ...el, isCompany: true })),
      ].flat();
    },
    shippingAddresses(): any[] {
      return this.flattenAddresses.filter((el) => el?.isShipTo === 'TRUE');
    },
    lastCreatedShippingAddress(): any {
      return this.shippingAddresses[this.shippingAddresses.length - 1] || '';
    },
    userCountry(s) {
      return s.countries.find((el) => el.isocode2 === s.me?.Country) || {};
    },
    Country(s) {
      return s.countries.find((el) => el.isocode2 === s.me?.Country) || {};
    },
    countryByName(s) {
      return (payload: any) => s.countries.find((el) => el.name === payload) || {};
    },
    billingAddresses(): any[] {
      return this.flattenAddresses.filter((el) => el?.isBillTo === 'TRUE');
    },
    lastCreatedBillingAddress() {
      return this.billingAddresses[this.billingAddresses.length] || '';
    },
    companyId(s) {
      return (s.me?.company || '').split('/').pop();
    },
    companyAllowPublicSellers(s) {
      return (s.company.PrivateMPAllowPublicSellers || 'false').toLowerCase() === 'true';
    },
    isPrivacyAccepted(s) {
      return s.privacyContracts.find(
        (el) => el.documentType === 'MP_DataPrivacyTCDocument' && el.currentState === 'IN_EFFECT',
      );
    },
    isDsBuyerPrivacyAccepted(s) {
      return s.privacyContracts.find(
        (el) => el.documentType === 'MP3DP_DSBuyersTCDocument' && el.currentState === 'IN_EFFECT',
      );
    },
    isSeller(s) {
      return (
        Boolean(s.me?.HasPublicSellerRoleAssigned === 'TRUE') ||
        Boolean(s.me?.PersonRoles.find((el) => el.Role === 'ShopOperator'))
      );
    },
    activeExternalContract(s): any {
      return s.externalPartContracts.find((el) => el.currentState === 'IN_EFFECT') || {};
    },
    isSharedPartsAccepted(): boolean {
      return Boolean(Object.keys(this.activeExternalContract).length);
    },
    roles(s) {
      return s.me?.PersonRoles || [];
    },
    hasRole() {
      return (role: string) => {
        switch (role) {
          case 'PublicSeller':
            return this.roles.some(
              (element) => element.Role === 'ShopOperator' && element.Project === 'Public',
            );
          case 'PrivateSeller':
            return this.roles.some(
              (element) =>
                (element.Role === 'ShopOperator' && element.Project !== 'Public') ||
                element.Role === 'Admin',
            );
          case 'Seller':
            return this.hasRole('PublicSeller');
          case 'PublicBuyer':
            return this.roles.some(
              (element) => element.Role === 'Buyer' && element.Project === 'Public',
            );
          case 'PrivateBuyer':
            return this.roles.some(
              (element) => element.Role === 'Buyer' && element.Project !== 'Public',
            );
          case 'Buyer':
            return this.hasRole('PublicBuyer') || this.hasRole('PrivateBuyer');
          case 'PrivateAdmin':
            return this.roles.some(
              (element) => element.Role === 'Admin' && element.Project !== 'Public',
            );
          case 'PrivateMPUser':
            return (
              this.hasRole('PrivateBuyer') ||
              this.hasRole('PrivateSeller') ||
              this.hasRole('PrivateAdmin')
            );
          case 'DSOperator':
            return this.roles.some(
              (element) => element.Role === 'DSOperator' && element.Project === 'Public',
            );
        }
      };
    },
    reducedShops(s) {
      return s.shops?.reduce((acc, el) => {
        acc[el.id] = el;
        return acc;
      }, {});
    },
  },
  actions: {
    setCountries(countries: any) {
      this.countries = countries || [];
    },
    setEstimatedUserLocation(estimatedUserLocation: string) {
      const { isoCode3 } = this.countries.find(
        (el: any) => el.isocode2 === estimatedUserLocation || el.isocode3 === estimatedUserLocation,
      );
      this.estimatedUserLocation = isoCode3;
    },
    async fetchMe(payload?: { force?: boolean }) {
      // If a fetch is already in progress, return that promise
      if (fetchMePromise && !payload?.force) {
        return fetchMePromise;
      }

      // If already fetched, return immediately
      if (this.isMeFetched && !payload?.force) {
        return this.me;
      }

      // Create and store the fetch promise
      fetchMePromise = (async () => {
        try {
          this.isMeFetched = false;
          const data = await $fetch.get('/resources/me', {
            noPassportRedirection: true,
            cache: true,
            force: payload?.force,
            noThrottle: payload?.force,
            query: {
              service: '3dprinting',
            },
          });
          await this.fetchExternalPartContracts();
          this.me = data as User;
          this.isMeFetched = true;
          return this.me;
        } catch (e) {
          this.isMeFetched = true;
          return undefined;
        } finally {
          // Clear the promise after completion
          fetchMePromise = undefined;
        }
      })();

      return fetchMePromise;
    },
    async fetchMeShops(payload: any = {}) {
      if (!this.me) {
        return;
      }

      this.shops = (await $fetch.get('/resources/me/shops', {
        force: payload?.force,
        cache: true,
        query: { service: '3dprinting' },
      })) as Lab[];
      this.setCurrentShop(this.shops.length ? String(this.shops[0].id) : '');
      await this.fetchExternalPartContracts();
    },
    setCurrentShop(id: string) {
      this.currentShop = id;
    },
    async fetchCompany(payload?: any) {
      if (!this.me?.company) {
        return;
      }

      const company = await $fetch.get(`/resources/mdpeoplecompany/companies/${this.companyId}`, {
        force: payload?.force,
        cache: true,
        query: {
          service: '3dprinting',
          expand: 'addresses',
        },
      });

      this.company = company;
    },
    async fetchAddresses(payload: any) {
      const addresses: any[] = (await $fetch.get('/resources/me/postal-addresses', {
        force: payload?.force,
        cache: Boolean(payload?.cache),
        query: {
          service: '3dprinting',
        },
      })) as any[];

      this.addresses = addresses;

      return addresses;
    },
    async fetchCountries() {
      const countries = (await $fetch.get('/resources/refs/countries', {
        cache: true,
        query: {
          service: '3dprinting',
        },
      })) as any[];

      this.countries = countries || [];

      await this.getUserLocationByIp();

      return countries;
    },
    async createAddress(payload: any): Promise<any> {
      const address = {
        addressLine0: null,
        addressLine1: payload.address.addressLine1,
        addressLine2: payload.address.addressLine2,
        addressLine3: payload.address.addressLine3,
        addressLine4: payload.address.addressLine4,
        city: payload.address.city,
        country: payload.address.country,
        county: '',
        isBillTo: payload.address.isBillTo || 'TRUE',
        isShipTo: 'TRUE',
        phoneNumber: payload.address.phoneNumber,
        state: payload.address.state,
        zipcode: payload.address.zipcode,
      };
      const url =
        payload.type === 'individual'
          ? '/resources/me/postal-addresses'
          : `/resources/mdpeoplecompany/companies/${this.me?.company}/postal-addresses`;
      const name =
        payload.type === 'individual'
          ? `${this.me?.firstName} ${this.me?.lastName}`
          : this.company.name;

      address.addressLine0 = name;

      try {
        await $fetch.post(url, { query: { service: '3dprinting' }, data: address });
        if (payload.type === 'individual') {
          this.addresses.push(address);
        } else {
          this.company.addresses.push(address);
        }

        return address;
      } catch (e) {
        if (payload.address.isBillTo) {
          throw new Error(e);
        } else {
          console.error(e);
        }

        return this.createAddress({
          type: payload.type,
          address: { isBillTo: 'FALSE', ...payload.address },
        });
      }
    },
    async linkContractDocumentToSeller(payload: any): Promise<any> {
      await this.fetchMe();

      return $fetch.post('/resources/mdcontract/contracts/existing-document', {
        query: { service: '3dprinting' },
        data: payload.consumerID
          ? {
              consumerID: payload.consumerID,
              documentID: payload.documentID,
              supplierID: this.me?.id,
            }
          : { documentID: payload.documentID, supplierID: this.me?.id },
      });
    },
    async getUserLocationByIp() {
      const tzArr = Intl.DateTimeFormat().resolvedOptions().timeZone.split('/');
      const userCity = tzArr[tzArr.length - 1];
      const country = this.countries.find((el) => el.name === timezones[userCity]);
      this.estimatedUserLocation = country ? country.isocode3 : 'US';
    },
    async waitUserIsConnected(timeout: number | undefined = 1000): Promise<void> {
      let stopWatch: () => void;

      if (this.isLoggedIn) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        stopWatch = watch(
          () => this.isLoggedIn,
          (connected) => {
            if (connected) {
              cleanup();
              resolve();
            }
          },
        );

        const interval = setInterval(() => {
          if (window.dsUserLogin) {
            if (!this.isLoggedIn && window.synchUser) {
              // Mutation directe dans Pinia
              this.me = window.synchUser;
              delete window.synchUser;
            }

            cleanup();
            resolve();
          }
        }, 200);

        const cleanup = () => {
          if (stopWatch) {
            stopWatch();
          }

          clearInterval(interval);
        };

        if (timeout) {
          setTimeout(() => {
            cleanup();
            resolve();
          }, timeout);
        }
      });
    },
    async fetchExternalPartContracts() {
      try {
        const contracts = await $fetch.get(
          '/resources/mdcontract/contracts/allows-external-part-upload',
          {
            cache: false,
            query: { service: '3dprinting', type: 'ds-buyer,data-privacy' },
          },
        );
        this.externalPartContracts = contracts as UserContract[];
      } catch (e) {
        this.externalPartContracts = [];
      }
    },
    /* SET the value of Shared Parts (is abled to share parts with external providers) */
    async setSharedParts(payload: boolean) {
      if (!this.me) {
        return;
      }

      if (payload) {
        const response = await $fetch.post(
          '/resources/mdcontract/contracts/allows-external-part-upload',
          {
            query: { service: '3dprinting' },
            data: {
              consumerID: this.me.id,
              currentState: 'IN_EFFECT',
            },
          },
        );
        this.contracts.push(response as UserContract);
      } else {
        const contract = this.contracts.find((el) => el.currentState === 'IN_EFFECT');
        if (contract?.id) {
          await $fetch.remove(`/resources/mdcontract/contracts/${contract.id}`, {
            data: {},
          });
          contract.currentState = 'DELETED';
        }
      }
    },
    /* Orders */
    async fetchMyRequests(payload: any = { start: 0, size: 10 }) {
      try {
        const myRequests: any = await $fetch.get('/resources/me/cartsV2', {
          cache: false,
          query: {
            service: '3dprinting',
            ...payload,
          },
        });

        return myRequests;
      } catch (e) {
        return [];
      }
    },
    /* Wait for USER to be fetched */
    async waitForMeIsFetched() {
      if (this.isMeFetched) {
        return;
      }

      return until(computed(() => this.isMeFetched)).toBe(true);
    },
    /* Wait for USER to be synchro with MyApps */
    async waitForMeSynchro() {
      if (!this.isMyAppsSynchronizing) {
        return;
      }

      return until(computed(() => this.isMyAppsSynchronizing)).toBe(false);
    },
  },
});
